// plugin-host.js - the GDBS Host SDK handed to every plugin's run()/render().
// Implements docs/PLUGIN_SPEC.txt section 4. A plugin never touches the raw
// engines/viewport/chart/trust APIs directly - it goes through this object, so
// the platform keeps one consistent, logged, trust-tracked surface.

import init, * as ENGINES from './engines.js';
import { trustHTML } from './geonum-trust.js';
import * as AUTH from './auth.js';
import { integrationPack, toCSV } from './plugin-integration.js';
import * as LEGAL from './legal-core.js';
import * as LEGAL_CLASSIFY from './legal-classify.js';

let _initPromise = null;
export function ensureEngines() {
  if (!_initPromise) { try { _initPromise = Promise.resolve(init()); } catch (e) { _initPromise = Promise.resolve(); } }
  return _initPromise;
}

const ENGINE_NAMES = Object.keys(ENGINES).filter(k => k.indexOf('run_') === 0).map(k => k.slice(4));

function callEngine(name, req) {
  const fn = ENGINES['run_' + name];
  if (typeof fn !== 'function') throw new Error('unknown GDBS engine: ' + name);
  const arg = (typeof req === 'string') ? req : JSON.stringify(req || {});
  const out = fn(arg);
  if (typeof out !== 'string') return out;
  try { return JSON.parse(out); } catch (e) { return out; }
}

// Worker bridge: run engine compute OFF the main thread (the generic
// engine-runner.worker.js loads engines.js and calls any run_* by name). This
// keeps the page responsive during a long wasm run, so the spinner animates and
// the browser never raises the "unresponsive script / wait or stop" dialog. If
// the worker can't be created, fall back to the synchronous main-thread path.
let _engWorker = null;
let _engReqId = 0;
const _engPending = new Map();
function engineWorker() {
  if (!_engWorker) {
    try {
      _engWorker = new Worker(new URL('engine-runner.worker.js', import.meta.url.replace(new RegExp('chunks/[^/]+$'), '')), { type: 'module' });
      _engWorker.onmessage = (e) => {
        const { id, ok, result, error } = e.data || {};
        const p = _engPending.get(id);
        if (!p) return;
        _engPending.delete(id);
        ok ? p.resolve(result) : p.reject(new Error(error));
      };
      _engWorker.onerror = (e) => {
        for (const p of _engPending.values()) p.reject(new Error((e && e.message) || 'engine worker error'));
        _engPending.clear();
        _engWorker = null; // let it be recreated on the next call
      };
    } catch (e) { _engWorker = null; }
  }
  return _engWorker;
}
function callEngineWorker(name, req) {
  const w = engineWorker();
  if (!w) return Promise.resolve(callEngine(name, req)); // no worker support -> main thread
  const arg = (typeof req === 'string') ? req : JSON.stringify(req || {});
  return new Promise((resolve, reject) => {
    const id = ++_engReqId;
    _engPending.set(id, {
      resolve: (out) => {
        if (typeof out !== 'string') return resolve(out);
        try { resolve(JSON.parse(out)); } catch (e) { resolve(out); }
      },
      reject,
    });
    w.postMessage({ id, fn: 'run_' + name, json: arg });
  });
}

// Terminate the running engine worker - an instant, real cancel of an in-flight
// compute (the worker dies, taking the wasm run with it). Pending calls reject;
// the worker is lazily recreated on the next engine() call.
export function cancelEngine() {
  if (_engWorker) {
    try { _engWorker.terminate(); } catch (e) {}
    for (const p of _engPending.values()) { try { p.reject(new Error('cancelled by user')); } catch (e) {} }
    _engPending.clear();
    _engWorker = null;
  }
}

// Tiny hyperscript helper so plugins can build DOM without a framework.
export function h(tag, attrs, ...kids) {
  const el = document.createElement(tag);
  if (attrs) for (const k in attrs) {
    if (k === 'style' && typeof attrs[k] === 'object') Object.assign(el.style, attrs[k]);
    else if (k === 'class' || k === 'className') el.className = attrs[k];
    else if (k.slice(0, 2) === 'on' && typeof attrs[k] === 'function') el.addEventListener(k.slice(2).toLowerCase(), attrs[k]);
    else if (k === 'html') el.innerHTML = attrs[k];
    else if (attrs[k] != null && attrs[k] !== false) el.setAttribute(k, attrs[k]);
  }
  for (const c of kids.flat()) { if (c == null || c === false) continue; el.appendChild(c.nodeType ? c : document.createTextNode(String(c))); }
  return el;
}

function license() { try { return AUTH.getLicense ? AUTH.getLicense() : (window.__gdbsLicense || null); } catch (e) { return null; } }
function user() { try { return AUTH.getUser ? AUTH.getUser() : null; } catch (e) { return null; } }

export function createHost(manifest, mountEl) {
  const logger = (window.gdbslog && window.gdbslog.getLogger) ? window.gdbslog.getLogger('plugin:' + manifest.id) : null;
  const host = {
    id: manifest.id,
    manifest: manifest,
    tier: manifest.tier,
    mount: mountEl,
    engines: ENGINE_NAMES.slice(),
    _lastResult: null, // raw result of the most recent engine() call - lets the
                       // loader auto-bind the GeoNum trust badge even when a plugin
                       // does not explicitly return a trust field.
    get user() { return user(); },
    get license() { return license(); },

    async engine(name, req) {
      // Compute runs in the worker, where gdbslog can't see it (no window). Log
      // it here on the main thread via gdbslog.time so the run is audited and
      // POSTed to /api/log/batch (the admin usage view). time() handles the
      // async worker promise and records the real duration + result telemetry.
      const t0 = (typeof performance !== 'undefined') ? performance.now() : Date.now();
      const glog = (typeof window !== 'undefined' && window.gdbslog) ? window.gdbslog.getLogger(name) : null;
      const call = () => callEngineWorker(name, req);
      const r = glog ? await glog.time(name, call) : await call();
      host._lastResult = r;
      // Plugins don't pass through engines.js _w, so capture the shared last-run
      // record here for the universal report + repro bundle.
      try {
        if (typeof window !== 'undefined') {
          const dt = (typeof performance !== 'undefined') ? (performance.now() - t0) : 0;
          const vid = window.gdbsReport && window.gdbsReport.findActiveArea ? (window.gdbsReport.findActiveArea().view?.id || null) : null;
          window.__gdbsLastRun = { domain: 'plugin', label: name, req, result: r, elapsed_ms: dt, viewId: vid, ts: Date.now() };
        }
      } catch (_) {}
      return r;
    },
    engineSync(name, req) {
      const r = callEngine(name, req); host._lastResult = r;
      try {
        if (typeof window !== 'undefined') {
          const vid = window.gdbsReport && window.gdbsReport.findActiveArea ? (window.gdbsReport.findActiveArea().view?.id || null) : null;
          window.__gdbsLastRun = { domain: 'plugin', label: name, req, result: r, viewId: vid, ts: Date.now() };
        }
      } catch (_) {}
      return r;
    },

    viewport3d(payload) {
      try {
        if (!payload) return;
        window.__gdbs3dPayload = payload;
        if (window.gdbs3D && typeof window.gdbs3D.open === 'function') window.gdbs3D.open(payload);
      } catch (e) { /* non-fatal */ }
    },

    chart(el, spec) {
      try {
        if (!el || !spec || !window.vchart) return;
        const t = spec.type || 'line';
        if (t === 'line' && window.vchart.line) return window.vchart.line(el, spec);
        if (t === 'scatter' && window.vchart.scatter) return window.vchart.scatter(el, spec);
        if (t === 'heatmap' && window.vchart.heatmap) return window.vchart.heatmap(el, spec);
        if (t === 'plot' && window.vchart.plot) return window.vchart.plot(el, spec.traces || [], spec.layout || {}, spec.config || {});
        if (t === 'frames' && window.vchart.frames) return window.vchart.frames(el, spec.traces || [], spec.layout || {}, spec.frames || [], spec.opts || {});
        if (window.vchart.line) return window.vchart.line(el, spec);
      } catch (e) { /* non-fatal */ }
    },

    trustHTML(result) { try { return trustHTML(result); } catch (e) { return ''; } },

    log(op, fields) { try { if (logger && logger.op) logger.op(op, fields || {}); } catch (e) {} },
    time(op, fn) { try { return (logger && logger.time) ? logger.time(op, fn) : fn(); } catch (e) { return fn(); } },

    // Shared "Integrate / embed" pack. A plugin passes its data (json/csv/embedHtml) and gets back
    // a consistent download + embed + subscribe UI, so any plugin can offer other applications a
    // way to bind to it without each one re-inventing the surface. See plugin-integration.js.
    integrationPack(spec) { try { return integrationPack(spec); } catch (e) { return { html: '<div style="color:#ef4444;font-size:12px;">integration pack error: ' + (e && e.message) + '</div>', summary: [], hrefs: {} }; } },
    toCSV: toCSV,

    // Shared legal substrate (date/holiday/FRCP engine, jurisdiction registry, trust, disclaimer,
    // citation + ambiguity helpers). Injected so the whole legal plugin suite computes deadlines,
    // counts court days, and cites rules through ONE audited, tested module. See legal-core.js.
    legal: LEGAL,
    // Deterministic client-side auto-classifier (practice area / category / priority + honest
    // confidence) for intake tickets, matters, and documents. See legal-classify.js.
    legalClassify: LEGAL_CLASSIFY,

    h: h,
  };
  return host;
}

if (typeof window !== 'undefined') {
  window.GDBS = window.GDBS || {};
  window.GDBS.createHost = createHost;
  window.GDBS.engines = ENGINE_NAMES.slice();
}
